import { Platform, StyleSheet } from 'react-native';

// Sofia Pro, loaded at runtime by App.js (useFonts) so it ships over the air.
// Each weight is its own family because Android ignores fontWeight for custom
// fonts; text that asks for a weight is mapped to the matching family.
export const BRAND_FONT_FAMILY = {
	regular: 'SofiaPro-Regular',
	medium: 'SofiaPro-Medium',
	semibold: 'SofiaPro-SemiBold',
	bold: 'SofiaPro-Bold',
};

export const BRAND_FONTS = {
	[BRAND_FONT_FAMILY.regular]: require('../assets/fonts/SofiaPro/SofiaProRegular.otf'),
	[BRAND_FONT_FAMILY.medium]: require('../assets/fonts/SofiaPro/SofiaProMedium.otf'),
	[BRAND_FONT_FAMILY.semibold]: require('../assets/fonts/SofiaPro/SofiaProSemiBold.otf'),
	[BRAND_FONT_FAMILY.bold]: require('../assets/fonts/SofiaPro/SofiaProBold.otf'),
};

const BRAND_FAMILIES = new Set(Object.values(BRAND_FONT_FAMILY));

const getWeightKey = (fontWeight) => {
	if (fontWeight === 'bold') {
		return 'bold';
	}
	const numericWeight = Number(fontWeight);
	if (!numericWeight || numericWeight < 500) {
		return 'regular';
	}
	if (numericWeight < 600) {
		return 'medium';
	}
	if (numericWeight < 700) {
		return 'semibold';
	}
	return 'bold';
};

/**
 * Sofia Pro family for a CSS-style weight ('400', '600', 'bold', 500 ...).
 * Callers pass whether the fonts loaded (theme.ui.areBrandFontsLoaded); while
 * they are not, this returns undefined so text keeps the system font.
 */
export const getBrandFontFamily = (fontWeight, areBrandFontsLoaded) => (
	areBrandFontsLoaded ? BRAND_FONT_FAMILY[getWeightKey(fontWeight)] : undefined
);

/**
 * Returns the style with fontWeight translated into the matching Sofia Pro
 * family. Styles that name another font family (e.g. monospace) are left alone.
 * Used by the shared Text and text input primitives.
 */
export const applyBrandFont = (style, areBrandFontsLoaded) => {
	if (!areBrandFontsLoaded) {
		return style;
	}
	const flatStyle = StyleSheet.flatten(style) || {};
	const { fontFamily, fontWeight } = flatStyle;
	if (fontFamily && (!BRAND_FAMILIES.has(fontFamily) || fontWeight === undefined)) {
		return style;
	}

	const brandStyle = { ...flatStyle, fontFamily: getBrandFontFamily(fontWeight, true) };
	delete brandStyle.fontWeight;
	return brandStyle;
};

// iOS and Android centre every line of Sofia Pro on its 750/-250 ascent/descent, so capitals
// (cap height 689 of 1000) sit 0.0945 em above the middle of the line box and look high
// next to icons and inside buttons and chips. Shared Text moves the drawn text down by that
// much; a transform leaves layout sizes unchanged. Text that draws its own box (border or
// background) moves only its content, through its padding, so the box stays in place.
// Measured on both phones; the web build places text differently.
const CAP_CENTRE_SHIFT_EM = (689 / 2 - (750 - 250) / 2) / 1000;
const DEFAULT_FONT_SIZE = 14;

export const withCapCentring = (style) => {
	if (Platform.OS === 'web') {
		return style;
	}
	const flatStyle = StyleSheet.flatten(style) || {};
	if (!BRAND_FAMILIES.has(flatStyle.fontFamily)) {
		return style;
	}
	const shift = (flatStyle.fontSize || DEFAULT_FONT_SIZE) * CAP_CENTRE_SHIFT_EM;
	const hasBorder = Boolean(flatStyle.borderWidth || flatStyle.borderTopWidth || flatStyle.borderBottomWidth);
	const hasBackground = Boolean(flatStyle.backgroundColor && flatStyle.backgroundColor !== 'transparent');
	if (hasBorder || hasBackground) {
		const paddingTop = flatStyle.paddingTop ?? flatStyle.paddingVertical ?? flatStyle.padding ?? 0;
		const paddingBottom = flatStyle.paddingBottom ?? flatStyle.paddingVertical ?? flatStyle.padding ?? 0;
		return [style, { paddingTop: paddingTop + shift, paddingBottom: Math.max(0, paddingBottom - shift) }];
	}
	return [style, { transform: [...(flatStyle.transform || []), { translateY: shift }] }];
};

// Type scale from the mockups. No text smaller than 12.
export const fontSize = {
	xs: 12, // tab labels, tags, captions
	sm: 13, // secondary lines
	md: 14, // small body
	row: 15, // row titles
	base: 16, // body, buttons, section titles
	lg: 19, // card titles, counters
	xl: 24, // screen titles
	xxl: 28, // hero numbers
};

const WEIGHT = {
	regular: '400',
	medium: '500',
	semibold: '600',
	bold: '700',
};

const presetDefinitions = {
	caption: { fontSize: fontSize.xs, lineHeight: 16, weight: 'regular' },
	tag: { fontSize: fontSize.xs, lineHeight: 16, weight: 'medium' },
	label: { fontSize: fontSize.xs, lineHeight: 16, weight: 'semibold' },
	overline: { fontSize: fontSize.xs, lineHeight: 16, weight: 'semibold', letterSpacing: 0.96, textTransform: 'uppercase' },
	secondary: { fontSize: fontSize.sm, lineHeight: 18, weight: 'regular' },
	secondaryStrong: { fontSize: fontSize.sm, lineHeight: 18, weight: 'medium' },
	secondarySemibold: { fontSize: fontSize.sm, lineHeight: 18, weight: 'semibold' },
	// Label above a form field (text input, selector, date); colour: colors.text.
	fieldLabel: { fontSize: fontSize.sm, lineHeight: 18, weight: 'semibold' },
	bodySmall: { fontSize: fontSize.md, lineHeight: 20, weight: 'regular' },
	bodySmallStrong: { fontSize: fontSize.md, lineHeight: 20, weight: 'medium' },
	bodySmallSemibold: { fontSize: fontSize.md, lineHeight: 20, weight: 'semibold' },
	rowText: { fontSize: fontSize.row, lineHeight: 20, weight: 'regular' },
	rowTitle: { fontSize: fontSize.row, lineHeight: 20, weight: 'medium' },
	rowTitleSemibold: { fontSize: fontSize.row, lineHeight: 20, weight: 'semibold' },
	body: { fontSize: fontSize.base, lineHeight: 22, weight: 'regular' },
	bodyStrong: { fontSize: fontSize.base, lineHeight: 22, weight: 'medium' },
	button: { fontSize: fontSize.base, lineHeight: 20, weight: 'medium' },
	sectionTitle: { fontSize: fontSize.base, lineHeight: 22, weight: 'semibold' },
	cardTitle: { fontSize: fontSize.lg, lineHeight: 24, weight: 'semibold' },
	title: { fontSize: fontSize.xl, lineHeight: 28, weight: 'semibold', letterSpacing: -0.24 },
	hero: { fontSize: fontSize.xxl, lineHeight: 32, weight: 'semibold' },
};

/**
 * Text style presets. When Sofia Pro is loaded each preset carries the
 * weight's family (and no fontWeight); otherwise it carries fontWeight so the
 * system font still shows the hierarchy.
 */
export const createTypePresets = (areBrandFontsLoaded) => Object.keys(presetDefinitions)
	.reduce((presets, key) => {
		const { weight, ...preset } = presetDefinitions[key];
		presets[key] = areBrandFontsLoaded
			? { ...preset, fontFamily: BRAND_FONT_FAMILY[weight] }
			: { ...preset, fontWeight: WEIGHT[weight] };
		return presets;
	}, {});
